import { useState, useMemo } from 'react';
import { SortField, Order } from '../types';
import { getCurrentMonth } from '../utils';
import { useDebounce } from './useDebounce';

export interface JournalFilterState {
  searchTerm: string;
  setSearchTerm: (v: string) => void;

  selectedMonth: string;
  setSelectedMonth: (v: string) => void;

  selectedCategory: string;
  setSelectedCategory: (v: string) => void;

  selectedManager: string;
  setSelectedManager: (v: string) => void;

  expiryFrom: string;
  setExpiryFrom: (v: string) => void;

  expiryTo: string;
  setExpiryTo: (v: string) => void;

  // ── Sort ──
  orderBy: SortField;
  order: Order;
  handleSort: (field: SortField) => void;

  // ── Pagination ──
  page: number;
  setPage: (v: number) => void;

  // ── Computed ──
  filterParams: Record<string, string>;

  // ── Actions ──
  handleReset: () => void;
}

/**
 * Управляет всеми состояниями фильтрации, сортировки и пагинации журнала.
 * Вычисляет `filterParams` - объект query-параметров для API-запросов.
 */
export function useJournalFilters(): JournalFilterState {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedManager, setSelectedManager] = useState('all');
  const [expiryFrom, setExpiryFrom] = useState('');
  const [expiryTo, setExpiryTo] = useState('');

  const [orderBy, setOrderBy] = useState<SortField>('recordDate');
  const [order, setOrder] = useState<Order>('desc');
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounce(searchTerm, 400);

  const filterParams = useMemo(() => {
    const p: Record<string, string> = {};
    if (selectedMonth && selectedMonth !== 'all') p.month = selectedMonth;
    if (selectedCategory !== 'all') p.category = selectedCategory;
    if (selectedManager !== 'all') p.manager = selectedManager;
    if (expiryFrom) p.expiryFrom = expiryFrom;
    if (expiryTo) p.expiryTo = expiryTo;
    if (debouncedSearch.trim()) p.search = debouncedSearch.trim();
    return p;
  }, [selectedMonth, selectedCategory, selectedManager, expiryFrom, expiryTo, debouncedSearch]);

  const handleSort = (field: SortField) => {
    setOrder(orderBy === field && order === 'asc' ? 'desc' : 'asc');
    setOrderBy(field);
    setPage(1);
  };

  const handleReset = () => {
    setSearchTerm('');
    setSelectedMonth('all');
    setSelectedCategory('all');
    setSelectedManager('all');
    setExpiryFrom('');
    setExpiryTo('');
    setPage(1);
  };

  return {
    searchTerm, setSearchTerm,
    selectedMonth, setSelectedMonth,
    selectedCategory, setSelectedCategory,
    selectedManager, setSelectedManager,
    expiryFrom, setExpiryFrom,
    expiryTo, setExpiryTo,
    orderBy, order, handleSort,
    page, setPage,
    filterParams,
    handleReset,
  };
}
